import React, { useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from './components/ui/Toaster';
import { AppProvider, useAppContext } from './contexts/AppContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Navbar } from './components/layout/Navbar';
import { Sidebar } from './components/layout/Sidebar';
import { LoadingSpinner } from './components/ui/LoadingSpinner';

// Lazy load all route components for better performance
const Dashboard = lazy(() => import('./pages/Dashboard').then(module => ({ default: module.Dashboard })));
const Inbox = lazy(() => import('./pages/Inbox').then(module => ({ default: module.Inbox })));
const Contacts = lazy(() => import('./pages/Contacts').then(module => ({ default: module.Contacts })));
const Groups = lazy(() => import('./pages/Groups').then(module => ({ default: module.Groups })));
const Campaigns = lazy(() => import('./pages/Campaigns').then(module => ({ default: module.Campaigns })));
const Flows = lazy(() => import('./pages/Flows').then(module => ({ default: module.Flows })));
const AutoReplies = lazy(() => import('./pages/AutoReplies').then(module => ({ default: module.AutoReplies })));
const Settings = lazy(() => import('./pages/Settings').then(module => ({ default: module.Settings })));
const LearnMore = lazy(() => import('./pages/LearnMore'));
const SetPassword = lazy(() => import('./pages/SetPassword'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
});

// Loading fallback component for lazy-loaded pages
const PageLoadingFallback = () => (
  <div className="flex justify-center items-center h-64">
    <div className="text-center">
      <LoadingSpinner size="lg" />
      <p className="text-muted-foreground mt-4">Loading...</p>
    </div>
  </div>
);

const AppContent: React.FC = () => {
  const { state } = useAppContext();
  const { state: authState } = useAuth();
  const location = useLocation();
  const isProfilePage = location.pathname.startsWith('/profile');
  const isLearnMorePage = location.pathname === '/learn-more';
  const isSetPasswordPage = location.pathname === '/set-password';
  const isForgotPasswordPage = location.pathname === '/forgot-password';
  const isResetPasswordPage = location.pathname.startsWith('/reset-password/');
  const isLoginRoute = location.pathname === '/login';

  useEffect(() => {
    if (state.darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [state.darkMode]);

  // Show public landing pages without authentication
  if (isLearnMorePage) {
    return (
      <Suspense fallback={<PageLoadingFallback />}>
        <LearnMore />
      </Suspense>
    );
  }

  // Public set-password page for invited users (no auth required)
  if (isSetPasswordPage) {
    return (
      <Suspense fallback={<PageLoadingFallback />}>
        <SetPassword />
      </Suspense>
    );
  }

  // Public forgot-password page (no auth required)
  if (isForgotPasswordPage) {
    return (
      <Suspense fallback={<PageLoadingFallback />}>
        <ForgotPassword />
      </Suspense>
    );
  }

  // Public reset-password page (no auth required)
  if (isResetPasswordPage) {
    return (
      <Suspense fallback={<PageLoadingFallback />}>
        <ResetPassword />
      </Suspense>
    );
  }

  // Show loading while checking authentication
  if (authState.loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Handle login route
  if (isLoginRoute) {
    if (authState.isAuthenticated) {
      // If already authenticated, redirect to dashboard
      return <Navigate to="/" replace />;
    } else {
      // Show login page
      return <Login />;
    }
  }

  // Show login page if not authenticated
  if (!authState.isAuthenticated) {
    return <Login />;
  }

  return (
    <div className="h-screen bg-background flex flex-col">
      <Navbar />
      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <main className={`flex-1 overflow-y-auto ${isProfilePage ? '' : 'p-6'}`}>
          <Suspense fallback={<PageLoadingFallback />}>
            <Routes>
              <Route path="/" element={<Dashboard />} />
              <Route path="/login" element={<Navigate to="/" replace />} />
              <Route path="/inbox" element={<Inbox />} />
              <Route path="/contacts" element={<Contacts />} />
              <Route path="/groups" element={<Groups />} />
              <Route path="/campaigns" element={<Campaigns />} />
              <Route path="/flows" element={<Flows />} />
              <Route path="/automatic-replies" element={<AutoReplies />} />
              <Route path="/integrations" element={<div className="text-center py-12"><h2 className="text-2xl font-bold text-foreground mb-4">Integrations</h2><p className="text-muted-foreground">Integrations are coming soon...</p></div>} />
              <Route path="/analytics" element={<div className="text-center py-12"><h2 className="text-2xl font-bold text-foreground mb-4">Analytics</h2><p className="text-muted-foreground">Advanced analytics coming soon...</p></div>} />
              <Route path="/learn-more" element={<LearnMore />} />
              <Route path="/profile/*" element={<Settings />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </div>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <AppProvider>
          <Router>
            <AppContent />
            <Toaster />
          </Router>
        </AppProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;